import React, { useState } from "react";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword } from "../api/apiClient";
import { passwordMeetsRules } from "./auth/passwordRules";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  if (!token) {
    return (
      <Shell>
        <Alert severity="error">
          Invalid reset link. Please request a new one.
        </Alert>
        <Button
          fullWidth
          variant="outlined"
          sx={{ mt: 2 }}
          onClick={() => navigate("/forgot-password")}
        >
          Request New Link
        </Button>
      </Shell>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!passwordMeetsRules(newPassword)) {
      setError("Password must be 8+ characters with an uppercase letter, a number and a symbol.");
      return;
    }
    if (newPassword !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, newPassword);
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "This link is invalid or has expired. Please request a new one.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      <Typography variant="h5" fontWeight={700} mb={1}>
        Set New Password
      </Typography>
      <Typography color="text.secondary" mb={3}>
        Use 8+ characters with an uppercase letter, a number and a symbol.
      </Typography>

      {success ? (
        <Alert severity="success">
          Password reset! Redirecting you to sign in…
        </Alert>
      ) : (
        <Box component="form" onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            label="New Password"
            type="password"
            fullWidth
            margin="normal"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            inputProps={{ minLength: 8 }}
            required
          />
          <TextField
            label="Confirm New Password"
            type="password"
            fullWidth
            margin="normal"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : "Reset Password"}
          </Button>
          <Button
            fullWidth
            variant="text"
            sx={{ mt: 1.5, color: "text.secondary" }}
            onClick={() => navigate("/login")}
          >
            Back to Sign In
          </Button>
        </Box>
      )}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Card>
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>{children}</CardContent>
        </Card>
      </Container>
    </Box>
  );
}
